import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { IconMagnifierOutline18 as Search } from "nucleo-ui-outline-18"
import { useMeasure } from "react-use"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { Avatar, Input, Tabs, TabsList, TabsTrigger } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { SupportPortalNavTab } from "./nav-target"
import { useShortcut } from "@/lib"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { PageHeader } from "../page-header"
import {
	getContactRowLabels,
	getNavTarget,
	supportPortalPath,
} from "./nav-target"

const ROW_CLASSNAME =
	"flex w-full min-w-0 items-center gap-2 rounded-md px-2 text-sm font-medium"
const CONTACT_ROW_SIZE = 43
const SEARCH_DEBOUNCE_MS = 200

export const SupportPortalNav = memo(
	({
		selectedCompanyId,
		selectedContactId,
	}: {
		selectedCompanyId?: string
		selectedContactId?: string
	}) => {
		const navigate = useNavigate()
		const [query, setQuery] = useState("")
		const [tab, setTab] = useState<SupportPortalNavTab>(
			selectedContactId ? "contacts" : "companies",
		)
		const [search, setSearch] = useState("")
		useEffect(() => {
			const timer = setTimeout(() => setSearch(query), SEARCH_DEBOUNCE_MS)
			return () => clearTimeout(timer)
		}, [query])
		const [companies] = useQuery(
			queries.companiesWithRequests({ query: search }),
			{ enabled: tab === "companies" },
		)
		const [contacts] = useQuery(
			queries.contactsWithRequests({ query: search }),
			{ enabled: tab === "contacts" },
		)
		const [listRef, { height: listHeight }] = useMeasure<HTMLDivElement>()

		// A deep link or a cmd+k jump decides the tab, not the local toggle.
		useEffect(() => {
			if (selectedContactId) setTab("contacts")
			else if (selectedCompanyId) setTab("companies")
		}, [selectedContactId, selectedCompanyId])

		const filteredCompanies = companies
		const filteredContacts = useMemo(
			() =>
				[...contacts].sort((a, b) =>
					(a.name || a.email).localeCompare(b.name || b.email),
				),
			[contacts],
		)

		// Arrow up/down cycles through the active list, like the inbox/roadmap.
		const goToOffset = useCallback(
			(offset: number) => {
				const target = getNavTarget({
					items: tab === "companies" ? filteredCompanies : filteredContacts,
					selectedId:
						tab === "companies" ? selectedCompanyId : selectedContactId,
					offset,
					tab,
				})
				if (target) void navigate({ href: target })
			},
			[
				tab,
				filteredCompanies,
				filteredContacts,
				selectedCompanyId,
				selectedContactId,
				navigate,
			],
		)

		useShortcut("ArrowDown", () => goToOffset(1), { preventDefault: true })
		useShortcut("ArrowUp", () => goToOffset(-1), { preventDefault: true })

		return (
			<div className="hidden h-full w-64 flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white sm:flex dark:border-gray-700/80 dark:bg-gray-900">
				<PageHeader
					title={
						<Link
							to="/support-portal"
							className="text-primary transition-colors hover:text-secondary"
						>
							Support portal
						</Link>
					}
					className="border-b border-gray-200 lg:pl-2.5 lg:pr-3 dark:border-gray-700/80"
					clearsNavToggle
					titleClassName="lg:ml-[7.5px]"
				/>
				<div className="border-b border-gray-200 px-2.5 py-2 dark:border-gray-700/80">
					<Tabs
						value={tab}
						onValueChange={(value) => {
							setTab(value as SupportPortalNavTab)
							setQuery("")
						}}
					>
						<TabsList>
							<TabsTrigger value="companies">Companies</TabsTrigger>
							<TabsTrigger value="contacts">Contacts</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
				<div className="mb-2 border-b border-gray-200 px-2.5 pb-2 pt-[5px] dark:border-gray-700/80">
					<div className="flex items-center gap-2 px-2">
						<Search className="h-4 w-4 flex-none text-secondary" />
						<Input
							name="support-portal-search"
							variant="borderless"
							textSize="sm"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={
								tab === "companies"
									? "Search companies..."
									: "Search contacts..."
							}
							className="px-0 placeholder:text-tertiary"
							inputClassName="h-7"
						/>
					</div>
				</div>
				{tab === "companies" ? (
					<div className="flex-1 overflow-y-auto px-2.5 pb-3">
						<ul className="flex list-none flex-col gap-px">
							{filteredCompanies.map((company) => {
								const isSelected = selectedCompanyId === company.id
								return (
									<li key={company.id}>
										<Link
											to={supportPortalPath("companies", company.id)}
											className={cn(
												ROW_CLASSNAME,
												"h-[1.9rem]",
												isSelected
													? "bg-gray-100 text-primary dark:bg-gray-800"
													: "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50",
											)}
										>
											<CompanyLogo
												linearLogoUrl={company.logoUrl ?? undefined}
												brandfetchData={null}
												fallback={company.name}
												size="xxs"
											/>
											<span className="flex-1 truncate text-start">
												{company.name}
											</span>
										</Link>
									</li>
								)
							})}
							{filteredCompanies.length === 0 && (
								<li className="px-2 py-4 text-center text-sm text-secondary">
									No companies found
								</li>
							)}
						</ul>
					</div>
				) : (
					<div ref={listRef} className="flex-1 overflow-hidden px-2.5">
						{filteredContacts.length === 0 ? (
							<div className="px-2 py-4 text-center text-sm text-secondary">
								No contacts found
							</div>
						) : (
							<FixedSizeList
								height={listHeight}
								width="100%"
								style={{ paddingBottom: 12 }}
								itemCount={filteredContacts.length}
								itemData={filteredContacts}
								itemKey={(index, data) => data[index]?.id ?? index}
								itemSize={CONTACT_ROW_SIZE}
								overscanCount={8}
							>
								{({ index, style }) => {
									const contact = filteredContacts[index]
									if (!contact) return null
									const isSelected = selectedContactId === contact.id
									const { primary, secondary } = getContactRowLabels(contact)
									return (
										<div style={style}>
											<Link
												to={supportPortalPath("contacts", contact.id)}
												className={cn(
													ROW_CLASSNAME,
													"h-10",
													isSelected
														? "bg-gray-100 text-primary dark:bg-gray-800"
														: "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50",
												)}
											>
												<Avatar
													size="xs"
													name={primary}
													image={contact.imageUrl}
												/>
												<span className="flex min-w-0 flex-1 flex-col justify-center text-start leading-tight">
													<span className="truncate">{primary}</span>
													{secondary && (
														<span className="truncate text-xs font-normal text-secondary">
															{secondary}
														</span>
													)}
												</span>
											</Link>
										</div>
									)
								}}
							</FixedSizeList>
						)}
					</div>
				)}
			</div>
		)
	},
)
